import type { Locale, MessageArgs } from '../types';
import {
  type DateStyle,
  type MessageNode,
  MessageSyntaxError,
  type NumberStyle,
  parseMessage,
  type TimeStyle,
} from './parse';

export interface FormatOptions {
  readonly locale: Locale;
  readonly timeZone?: string | undefined;
}

const PARSE_CACHE_LIMIT = 2000;
const parseCache = new Map<string, readonly MessageNode[]>();

/** Parses once per distinct source; a message that does not parse renders as its source. */
export function parseCached(source: string): readonly MessageNode[] {
  const hit = parseCache.get(source);
  if (hit) return hit;
  let nodes: readonly MessageNode[];
  try {
    nodes = parseMessage(source);
  } catch (error) {
    if (!(error instanceof MessageSyntaxError)) throw error;
    nodes = [{ kind: 'text', value: source }];
  }
  if (parseCache.size >= PARSE_CACHE_LIMIT) {
    const oldest = parseCache.keys().next().value;
    if (oldest !== undefined) parseCache.delete(oldest);
  }
  parseCache.set(source, nodes);
  return nodes;
}

export function formatMessage(
  source: string,
  args: MessageArgs,
  options: FormatOptions,
): string {
  return formatNodes(parseCached(source), args, options);
}

export function formatNodes(
  nodes: readonly MessageNode[],
  args: MessageArgs,
  options: FormatOptions,
): string {
  return render(nodes, args, options, undefined);
}

function render(
  nodes: readonly MessageNode[],
  args: MessageArgs,
  options: FormatOptions,
  pound: number | undefined,
): string {
  let out = '';
  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        out += node.value;
        break;
      case 'pound':
        out +=
          pound === undefined
            ? '#'
            : numberFormatter(options.locale, 'decimal').format(pound);
        break;
      case 'arg':
        out += renderArg(node.name, args);
        break;
      case 'number': {
        const value = toNumber(args[node.name]);
        out +=
          value === undefined
            ? missing(node.name)
            : numberFormatter(options.locale, node.style).format(value);
        break;
      }
      case 'date': {
        const value = toDate(args[node.name]);
        out +=
          value === undefined
            ? missing(node.name)
            : dateFormatter(options, node.style, undefined).format(value);
        break;
      }
      case 'time': {
        const value = toDate(args[node.name]);
        out +=
          value === undefined
            ? missing(node.name)
            : dateFormatter(options, undefined, node.style).format(value);
        break;
      }
      case 'select': {
        const value = args[node.name];
        const key =
          value === undefined || value === null ? 'other' : String(value);
        const branch = node.options[key] ?? node.options.other ?? [];
        out += render(branch, args, options, pound);
        break;
      }
      case 'plural': {
        const value = toNumber(args[node.name]);
        if (value === undefined) {
          out += missing(node.name);
          break;
        }
        const adjusted = value - node.offset;
        const exact = node.options[`=${value}`];
        const category = pluralRules(options.locale).select(adjusted);
        const branch =
          exact ?? node.options[category] ?? node.options.other ?? [];
        out += render(branch, args, options, adjusted);
        break;
      }
    }
  }
  return out;
}

function renderArg(name: string, args: MessageArgs): string {
  const value = args[name];
  if (value === undefined || value === null) return missing(name);
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function missing(name: string): string {
  return `{${name}}`;
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function toDate(value: unknown): Date | undefined {
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof value === 'number' || typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
}

const pluralCache = new Map<string, Intl.PluralRules>();
function pluralRules(locale: Locale): Intl.PluralRules {
  let rules = pluralCache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralCache.set(locale, rules);
  }
  return rules;
}

const numberCache = new Map<string, Intl.NumberFormat>();
export function numberFormatter(
  locale: Locale,
  style: NumberStyle,
): Intl.NumberFormat {
  const key = `${locale}|${typeof style === 'string' ? style : `currency:${style.currency}`}`;
  let formatter = numberCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, numberOptions(style));
    numberCache.set(key, formatter);
  }
  return formatter;
}

function numberOptions(style: NumberStyle): Intl.NumberFormatOptions {
  if (typeof style !== 'string')
    return { style: 'currency', currency: style.currency };
  switch (style) {
    case 'integer':
      return { maximumFractionDigits: 0 };
    case 'percent':
      return { style: 'percent', maximumFractionDigits: 1 };
    case 'decimal':
      return {};
  }
}

const dateCache = new Map<string, Intl.DateTimeFormat>();
export function dateFormatter(
  options: FormatOptions,
  dateStyle: DateStyle | undefined,
  timeStyle: TimeStyle | undefined,
): Intl.DateTimeFormat {
  const key = `${options.locale}|${dateStyle ?? ''}|${timeStyle ?? ''}|${options.timeZone ?? ''}`;
  let formatter = dateCache.get(key);
  if (!formatter) {
    const init: Intl.DateTimeFormatOptions = {};
    if (dateStyle) init.dateStyle = dateStyle;
    if (timeStyle) init.timeStyle = timeStyle;
    if (options.timeZone) init.timeZone = options.timeZone;
    formatter = new Intl.DateTimeFormat(options.locale, init);
    dateCache.set(key, formatter);
  }
  return formatter;
}
